import { useMutation } from "@apollo/client/react";
import { Button, Dropdown, type MenuProps, Space } from "antd";
import { freeze } from "immer";
import { useTranslation } from "react-i18next";
import { DownOutlined } from "@ant-design/icons";
import type { ElementOf } from "ts-essentials";
import { UpdateUserDocument } from "../gql/generated/graphqlSchema.tsx";

const LANGUAGES = freeze(
  [
    { flag: "🇬🇧", key: "en", label: "English" },
    { flag: "🇧🇬", key: "bg", label: "Български" },
  ] as const,
  true,
);

type Props = {
  readonly collapsed?: boolean;
  readonly currentUser?: { locale: string } | null;
};

export const LanguageSwitcher = ({ collapsed = false, currentUser = null }: Props) => {
  const { i18n } = useTranslation();
  const [updateUser] = useMutation(UpdateUserDocument);
  const current = LANGUAGES.find((l) => i18n.language.startsWith(l.key)) ?? LANGUAGES[0];

  const items = LANGUAGES.map<ElementOf<NonNullable<MenuProps["items"]>>>((lang) => ({
    key: lang.key,
    label: (
      <>
        {lang.flag} {lang.label}
      </>
    ),
    onClick: () => {
      void i18n.changeLanguage(lang.key);
      if (currentUser && currentUser.locale !== lang.key) {
        void updateUser({
          variables: { input: { locale: lang.key } },
        });
      }
    },
  }));

  return (
    <Dropdown arrow={collapsed} destroyOnHidden menu={{ items, selectedKeys: [current.key] }} trigger={["click"]}>
      <Button shape={collapsed ? "circle" : undefined}>
        <Space>
          {current.flag}
          {!collapsed && (
            <>
              {current.label}
              <DownOutlined />
            </>
          )}
        </Space>
      </Button>
    </Dropdown>
  );
};
